import { useState } from "react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/features/auth/AuthContext";
import { supabase } from "@/lib/supabase";
import { uploadUserFile } from "@/lib/storage";
import { PLANS, type PlanKey, MONTHLY_CREDITS } from "@/lib/constants";

// InstaPay handle shown to users (PRD §3.3 Phase 1). Replace with the real one.
const INSTAPAY_HANDLE = "vira@instapay";

export function AccountPage() {
  const { t, lang } = useI18n();
  const { session, wallet, subscription, hasActiveSub, refresh } = useAuth();
  const uid = session!.user.id;

  const [selectedPlan, setSelectedPlan] = useState<PlanKey | null>(null);
  const [uploading, setUploading] = useState(false);
  const [proofDone, setProofDone] = useState(false);

  async function startPlan(plan: PlanKey) {
    setSelectedPlan(plan);
    // Create / refresh a pending subscription row so admin can see intent.
    await supabase.from("subscriptions").insert({
      user_id: uid,
      plan,
      status: "pending",
      provider: "manual",
    });
  }

  async function uploadProof(file: File) {
    setUploading(true);
    try {
      const url = await uploadUserFile("payment-proofs", uid, file);
      await supabase.from("subscription_events").insert({
        user_id: uid,
        type: "payment_proof_uploaded",
        actor: "system",
        meta: { proof_url: url, plan: selectedPlan },
      });
      setProofDone(true);
      await refresh();
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <h1 className="mb-4 text-xl font-bold">{t("account")}</h1>

      {/* Credits + subscription status */}
      <section className="card mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted">{t("creditsRemaining")}</span>
          <span className="text-2xl font-bold text-accent">{wallet?.balance ?? 0}</span>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-sm text-muted">{t("subscription")}</span>
          <span className="text-sm font-medium">
            {hasActiveSub
              ? `${subscription?.plan} · ${subscription?.current_period_end?.slice(0, 10)}`
              : subscription?.status === "pending"
                ? t("proofUploaded")
                : "—"}
          </span>
        </div>
      </section>

      {/* Plan selection (only if not active) */}
      {!hasActiveSub && (
        <section className="card mb-4">
          <h2 className="mb-3 text-base font-semibold">{t("selectPlan")}</h2>
          <div className="grid gap-3">
            {(Object.keys(PLANS) as PlanKey[]).map((key) => {
              const p = PLANS[key];
              const on = selectedPlan === key;
              return (
                <button
                  key={key}
                  className={`rounded-2xl border p-4 text-start ${on ? "border-accent bg-accent-soft" : "border-slate-200"}`}
                  onClick={() => startPlan(key)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{lang === "ar" ? p.label : p.labelEn}</span>
                    <span className="font-bold text-accent">{p.priceEGP} EGP</span>
                  </div>
                  <p className="mt-1 text-xs text-muted">
                    {MONTHLY_CREDITS} {t("creditsRemaining")} {t("perMonth")}
                  </p>
                </button>
              );
            })}
          </div>

          {selectedPlan && (
            <div className="mt-4 rounded-xl bg-slate-50 p-4">
              <h3 className="mb-2 text-sm font-semibold">{t("instapayInstructions")}</h3>
              <p className="text-sm text-muted">
                {lang === "ar"
                  ? `حوّل مبلغ ${PLANS[selectedPlan].priceEGP} جنيه إلى `
                  : `Transfer ${PLANS[selectedPlan].priceEGP} EGP to `}
                <span dir="ltr" className="font-mono font-semibold">
                  {INSTAPAY_HANDLE}
                </span>
              </p>
              <div className="mt-3">
                {proofDone ? (
                  <p className="text-sm font-medium text-accent">✓ {t("proofUploaded")}</p>
                ) : (
                  <label className="btn-primary w-full cursor-pointer">
                    {uploading ? t("saving") : t("uploadProof")}
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      hidden
                      onChange={(e) => e.target.files?.[0] && uploadProof(e.target.files[0])}
                    />
                  </label>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      <button
        className="btn-ghost w-full text-red-500"
        onClick={() => supabase.auth.signOut()}
      >
        {t("logout")}
      </button>
    </div>
  );
}
